import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Megaphone, Plus, PencilSimple, CalendarDots } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_COLORS = { draft: '#F59E0B', active: '#10B981', paused: '#3B82F6', completed: '#555' };
const TYPES = ['learn_to_play', 'expansion_launch', 'community_event', 'featured_placement', 'beginner_week'];

export default function PublisherCampaigns() {
  const { publisherId } = useParams();
  const [campaigns, setCampaigns] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', campaign_type: 'learn_to_play', game_id: '', starts_at: '', ends_at: '' });

  const fetch = () => {
    axios.get(`${API_URL}/api/publisher-portal/${publisherId}/campaigns`, { withCredentials: true }).then(r => setCampaigns(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    axios.get(`${API_URL}/api/publisher-portal/${publisherId}/games`, { withCredentials: true }).then(r => setGames(r.data || [])).catch(() => {});
  };
  useEffect(fetch, [publisherId]);

  const create = async () => {
    if (!form.title) { toast.error('Title required'); return; }
    try {
      await axios.post(`${API_URL}/api/publisher-portal/${publisherId}/campaigns`, form, { withCredentials: true });
      toast.success('Campaign created'); setShowCreate(false);
      setForm({ title: '', description: '', campaign_type: 'learn_to_play', game_id: '', starts_at: '', ends_at: '' });
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/publisher-portal/${publisherId}/campaigns/${id}`, { status }, { withCredentials: true });
      toast.success(`Status: ${status}`); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const inputCls = "w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm focus:border-[#F59E0B]/50 focus:outline-none placeholder-[#444]";

  return (
    <div data-testid="publisher-campaigns-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Campaigns</h1>
          <p className="text-sm text-[#666] mt-1">{campaigns.length} campaigns</p>
        </div>
        <button onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#F59E0B] text-black font-semibold text-sm"
          data-testid="create-campaign-btn">
          <Plus size={16} weight="bold" /> New Campaign
        </button>
      </div>

      {showCreate && (
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 mb-6 space-y-4">
          <input className={inputCls} placeholder="Campaign title" value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })} data-testid="campaign-title-input" />
          <textarea className={inputCls + " h-20 resize-none"} placeholder="Description..."
            value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          <div className="grid grid-cols-3 gap-3">
            <select className={inputCls} value={form.campaign_type} onChange={e => setForm({ ...form, campaign_type: e.target.value })}>
              {TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
            </select>
            <input className={inputCls} type="date" placeholder="Start date" value={form.starts_at}
              onChange={e => setForm({ ...form, starts_at: e.target.value })} />
            <input className={inputCls} type="date" placeholder="End date" value={form.ends_at}
              onChange={e => setForm({ ...form, ends_at: e.target.value })} />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-[#888]">Cancel</button>
            <button onClick={create} className="px-5 py-2 rounded-lg bg-[#F59E0B] text-black font-semibold text-sm"
              data-testid="campaign-submit-btn">Create</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {loading ? [...Array(2)].map((_, i) => <div key={i} className="h-24 bg-white/5 rounded-xl animate-pulse" />) :
        campaigns.length === 0 ? (
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-10 text-center">
            <Megaphone size={44} className="mx-auto text-[#333] mb-4" />
            <p className="text-white font-medium text-lg mb-1">No Campaigns Yet</p>
            <p className="text-sm text-[#555]">Launch learn-to-play campaigns and promotions.</p>
          </div>
        ) : campaigns.map(c => (
          <div key={c.id} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <Megaphone size={20} weight="duotone" className="text-[#F59E0B]" />
                <div>
                  <p className="text-white font-semibold">{c.title}</p>
                  <div className="flex items-center gap-2 text-[11px] text-[#555]">
                    <span className="capitalize">{(c.campaign_type || '').replace(/_/g, ' ')}</span>
                    {c.starts_at && <span><CalendarDots size={10} className="inline" /> {new Date(c.starts_at).toLocaleDateString()}</span>}
                    {c.ends_at && <span>- {new Date(c.ends_at).toLocaleDateString()}</span>}
                  </div>
                </div>
              </div>
              <span className="text-[11px] px-2.5 py-1 rounded-full font-medium"
                style={{ backgroundColor: `${STATUS_COLORS[c.status] || '#555'}15`, color: STATUS_COLORS[c.status] || '#555' }}>
                {c.status}
              </span>
            </div>
            {c.description && <p className="text-sm text-[#888] mt-1 line-clamp-2">{c.description}</p>}
            <div className="flex gap-2 mt-3 pt-3 border-t border-white/[0.04]">
              {c.status === 'draft' && <button onClick={() => updateStatus(c.id, 'active')} className="text-xs px-3 py-1.5 rounded-lg bg-[#10B981]/10 text-[#10B981] font-medium">Activate</button>}
              {c.status === 'active' && <button onClick={() => updateStatus(c.id, 'paused')} className="text-xs px-3 py-1.5 rounded-lg bg-[#3B82F6]/10 text-[#3B82F6] font-medium">Pause</button>}
              {c.status === 'active' && <button onClick={() => updateStatus(c.id, 'completed')} className="text-xs px-3 py-1.5 rounded-lg bg-white/5 text-[#888] font-medium">Complete</button>}
              {c.status === 'paused' && <button onClick={() => updateStatus(c.id, 'active')} className="text-xs px-3 py-1.5 rounded-lg bg-[#10B981]/10 text-[#10B981] font-medium">Resume</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
